import {useEffect, useRef} from 'react';
import {useSearchParams} from 'react-router-dom';
import {getCookie, setCookie} from './use-cookie';

export const TRACKING_PARAMETERS_COOKIE = 'TRACKING_PARAMETERS';

export type TrackingParameters = Record<string, string>;

export function getTrackingParametersFromCookie():
  | TrackingParameters
  | undefined {
  const raw = getCookie(TRACKING_PARAMETERS_COOKIE);
  if (!raw) return undefined;

  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed as TrackingParameters;
    }
  } catch {
    // ignore invalid cookie value
  }

  return undefined;
}

export function setTrackingParametersCookie(params: TrackingParameters): void {
  if (!params || Object.keys(params).length === 0) return;
  setCookie(TRACKING_PARAMETERS_COOKIE, JSON.stringify(params));
}

export function captureTrackingParametersFromSearchParams(
  searchParams: URLSearchParams,
  options: {requireGclid?: boolean} = {},
): TrackingParameters | undefined {
  const params = Object.fromEntries(searchParams.entries());
  if (Object.keys(params).length === 0) return undefined;
  if (options.requireGclid && !searchParams.get('gclid')) {
    return undefined;
  }
  setTrackingParametersCookie(params);
  return params;
}

export function useTrackingParameters(options: {
  requireGclid?: boolean;
  onCapture?: (params: TrackingParameters) => void;
} = {requireGclid: true}) {
  const [searchParams] = useSearchParams();
  const requireGclid = options.requireGclid ?? true;
  const onCapture = options.onCapture;
  const lastCaptureRef = useRef<string | null>(null);

  useEffect(() => {
    const captured = captureTrackingParametersFromSearchParams(searchParams, {
      requireGclid,
    });
    if (!captured || !onCapture) return;
    const captureKey = JSON.stringify(captured);
    if (lastCaptureRef.current === captureKey) return;
    lastCaptureRef.current = captureKey;
    onCapture(captured);
  }, [searchParams, requireGclid, onCapture]);
}
